import { useCallback, useEffect, useRef } from 'react';

import { Button, Typography } from '@braskai/ui-kit';

import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { Box, Stack } from '@mui/material';

import G2Logo from '@/shared/assets/icons/g2-logo.svg';
import BoschLogotype from '@/shared/assets/images/new-user-quiz/bosch-logotype.svg';
import BoschSymbol from '@/shared/assets/images/new-user-quiz/bosch-symbol.svg';
import CnbcLogo from '@/shared/assets/images/new-user-quiz/cnbc.png';
import HarvardLogo from '@/shared/assets/images/new-user-quiz/harvard.svg';
import SiemensLogo from '@/shared/assets/images/new-user-quiz/siemens.svg';
import RaskLogo from '@/shared/assets/images/rask.svg';
import { BOOK_A_DEMO_MEETING_LINK } from '@/shared/constants/docLinks';
import { enqueueAppSnackbar } from '@/shared/lib/snackbar';
import { HubSpotMeetings } from '@/shared/thirdParty/hubSpot';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { AlertType } from '@/shared/types/Alert';

import { NEW_USER_QUIZ_TEST_IDS } from '../lib/constants';
import { getNewUserQuizLocalization, type NewUserQuizLocalization } from '../lib/i18n';

const BULLETS = [
  { Icon: LanguageOutlinedIcon, key: 'setup' },
  { Icon: GroupsOutlinedIcon, key: 'teams' },
  { Icon: SellOutlinedIcon, key: 'pricing' },
] as const;

const NEW_USER_QUIZ_BOOKING_EVENT_PROPERTIES = { source: 'new_user_quiz' } as const;

const SocialProof = ({ rating }: { rating: string }) => (
  <Stack
    alignItems="center"
    direction={{ desktop: 'row', mobile: 'column' }}
    gap={{ desktop: '12px', mobile: '8px' }}
    justifyContent="space-between"
    minHeight={{ desktop: '32px', mobile: '60px' }}
    width="100%"
  >
    <Stack alignItems="center" direction="row" gap="8px">
      <Box alt="G2" component="img" height="24px" src={G2Logo} width="24px" />
      <Stack direction="row">
        {Array.from({ length: 5 }, (_, index) => (
          <StarRoundedIcon key={index} sx={{ color: 'warning.500', fontSize: '16px' }} />
        ))}
      </Stack>
      <Typography color="grey.500" variant="body-2-md">
        {rating}
      </Typography>
    </Stack>

    <Box sx={{ bgcolor: 'grey.200', display: { desktop: 'block', mobile: 'none' }, height: '24px', width: '1px' }} />

    <Stack alignItems="center" direction="row" gap="12px">
      <VerifiedUserOutlinedIcon sx={{ color: 'grey.700', fontSize: '20px' }} />
      <Typography color="grey.500" variant="caption-1-md">
        SOC 2 Type II · GDPR · SSO/SAML
      </Typography>
    </Stack>
  </Stack>
);

const Partners = ({ trustedBy }: { trustedBy: string }) => (
  <Stack alignItems={{ desktop: 'flex-start', mobile: 'center' }} gap={{ desktop: '16px', mobile: '8px' }} width="100%">
    <Typography color="grey.500" textAlign={{ desktop: 'left', mobile: 'center' }} variant="body-1-md" width="100%">
      {trustedBy}
    </Typography>
    <Box
      sx={{
        alignItems: 'center',
        display: 'grid',
        gap: { desktop: '24px', mobile: '12px 24px' },
        gridTemplateColumns: { desktop: 'repeat(4, auto)', mobile: 'repeat(2, 114px)' },
        justifyItems: 'center',
        minHeight: { desktop: '32px', mobile: '68px' },
      }}
    >
      <Box
        aria-label="CNBC"
        role="img"
        sx={{ height: '18px', overflow: 'hidden', position: 'relative', width: '54.75px' }}
      >
        <Box
          alt=""
          component="img"
          src={CnbcLogo}
          sx={{ height: '30.69px', left: '-0.07px', position: 'absolute', top: '-6.59px', width: '54.59px' }}
        />
      </Box>
      <Box alt="Siemens" component="img" height="14.435px" src={SiemensLogo} width="87.3px" />
      <Box alt="Harvard University" component="img" height="21.572px" src={HarvardLogo} width="85.95px" />
      <Box aria-label="Bosch" role="img" sx={{ height: '21.573px', position: 'relative', width: '96.3px' }}>
        <Box
          alt=""
          component="img"
          src={BoschSymbol}
          sx={{ height: '21.44px', left: 0, position: 'absolute', top: '0.04px', width: '21.44px' }}
        />
        <Box
          alt=""
          component="img"
          src={BoschLogotype}
          sx={{ height: '14.86px', left: '32.11px', position: 'absolute', top: '3.26px', width: '64.19px' }}
        />
      </Box>
    </Box>
  </Stack>
);

type NewUserQuizBookingProps = {
  email?: string;
  localization?: NewUserQuizLocalization;
  onDismiss: () => void;
};

export const NewUserQuizBooking = ({
  email,
  localization = getNewUserQuizLocalization(),
  onDismiss,
}: NewUserQuizBookingProps) => {
  const bookedRef = useRef(false);
  const embedErrorTrackedRef = useRef(false);
  const openedEventTrackedRef = useRef(false);
  const { copy, locale } = localization;
  const { booking } = copy;

  useEffect(() => {
    if (!openedEventTrackedRef.current) {
      openedEventTrackedRef.current = true;
      MixpanelEvents.bookACallOpened(NEW_USER_QUIZ_BOOKING_EVENT_PROPERTIES);
    }
  }, []);

  const handleBooked = useCallback(() => {
    if (bookedRef.current) {
      return;
    }

    bookedRef.current = true;
    MixpanelEvents.bookACallBooked(NEW_USER_QUIZ_BOOKING_EVENT_PROPERTIES);
    enqueueAppSnackbar(booking.bookedSnackbar, { variant: AlertType.Success });
    onDismiss();
  }, [booking.bookedSnackbar, onDismiss]);

  const handleMaybeLater = useCallback(() => {
    MixpanelEvents.bookACallSkipped(NEW_USER_QUIZ_BOOKING_EVENT_PROPERTIES);
    MixpanelEvents.bookACallMaybeLaterClicked(NEW_USER_QUIZ_BOOKING_EVENT_PROPERTIES);
    onDismiss();
  }, [onDismiss]);

  const handleEmbedLoadError = useCallback(() => {
    if (embedErrorTrackedRef.current) {
      return;
    }

    embedErrorTrackedRef.current = true;
    MixpanelEvents.bookACallEmbedFailed(NEW_USER_QUIZ_BOOKING_EVENT_PROPERTIES);
  }, []);

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'common.white',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: { desktop: 'row', mobile: 'column' },
        gap: { desktop: 0, mobile: '32px' },
        justifyContent: 'center',
        minHeight: '100dvh',
        overflowY: 'auto',
        pb: { desktop: 0, mobile: '56px' },
        pt: { desktop: '32px', mobile: '80px' },
        px: { desktop: 0, mobile: '16px' },
        width: '100%',
      }}
      component="main"
      data-testid={NEW_USER_QUIZ_TEST_IDS.booking}
      lang={locale}
    >
      <Stack
        sx={{
          boxSizing: 'border-box',
          height: { desktop: '720px', mobile: 'auto' },
          maxHeight: { desktop: '100dvh', mobile: 'none' },
          px: { desktop: '24px', mobile: 0 },
          py: { desktop: '40px', mobile: 0 },
          width: { desktop: '620px', mobile: '100%' },
        }}
        gap={{ desktop: '32px', mobile: 0 }}
        justifyContent={{ desktop: 'space-between', mobile: 'flex-start' }}
      >
        <Stack gap={{ desktop: '32px', mobile: '24px' }} width="100%">
          <Stack gap={{ desktop: '48px', mobile: '32px' }}>
            <SocialProof rating={booking.rating} />

            <Stack
              sx={{
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                pb: { desktop: '32px', mobile: '24px' },
              }}
              alignItems="center"
              gap="24px"
              width="100%"
            >
              <Box alt="Rask" component="img" height="22px" src={RaskLogo} width="132px" />

              <Stack
                gap={{ desktop: '8px', mobile: '12px' }}
                mt={{ desktop: 0, mobile: '-8px' }}
                textAlign={{ desktop: 'left', mobile: 'center' }}
                width="100%"
              >
                <Typography
                  sx={{
                    fontSize: { desktop: '36px', mobile: '24px' },
                    lineHeight: { desktop: '44px', mobile: '30px' },
                  }}
                  color="grey.900"
                  component="h1"
                  variant="title-1-md"
                >
                  {booking.heading}
                  <Box component="span" sx={{ display: { desktop: 'none', mobile: 'inline' } }}>
                    .
                  </Box>
                  <br />
                  {booking.headingSecondLine}
                </Typography>
                <Typography color="grey.500" variant="body-1-md">
                  {booking.description}
                </Typography>
              </Stack>

              <Stack gap={{ desktop: '20px', mobile: '16px' }} width="100%">
                {BULLETS.map(({ Icon, key }) => (
                  <Stack key={key} alignItems="center" direction="row" gap="12px" px="4px">
                    <Icon sx={{ color: 'grey.700', flexShrink: 0, fontSize: '20px' }} />
                    <Typography color="grey.900" variant="body-2-md">
                      {booking.bullets[key]}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>

          <Partners trustedBy={booking.trustedBy} />
        </Stack>

        <Button
          data-testid={NEW_USER_QUIZ_TEST_IDS.maybeLater}
          size="md"
          sx={{ alignSelf: 'center', display: { desktop: 'inline-flex', mobile: 'none' }, width: '200px' }}
          variant="outlined"
          onClick={handleMaybeLater}
        >
          {booking.maybeLater}
        </Button>
      </Stack>

      <Box
        sx={{
          '& .meetings-iframe-container': { height: '100%', width: '100%' },
          '& .meetings-iframe-container iframe': {
            border: 0,
            display: 'block',
            height: '100% !important',
            width: '100% !important',
          },
          flexShrink: 0,
          height: { desktop: 'calc(100dvh - 40px)', mobile: '692px' },
          maxHeight: { desktop: '680px', mobile: 'none' },
          maxWidth: { desktop: 'none', mobile: '350px' },
          position: 'relative',
          width: { desktop: 'min(660px, calc(100vw - 620px))', mobile: '100%' },
        }}
      >
        <HubSpotMeetings
          copy={booking.scheduler}
          email={email}
          meetingUrl={BOOK_A_DEMO_MEETING_LINK}
          onBooked={handleBooked}
          onLoadError={handleEmbedLoadError}
        />
      </Box>

      <Button
        data-testid={`${NEW_USER_QUIZ_TEST_IDS.maybeLater}-mobile`}
        size="md"
        sx={{ display: { desktop: 'none', mobile: 'inline-flex' }, width: '200px' }}
        variant="outlined"
        onClick={handleMaybeLater}
      >
        {booking.maybeLater}
      </Button>
    </Box>
  );
};
